import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import ScrollTopButton from "@/components/base/ScrollTopButton";
import JsonLd from "@/components/feature/JsonLd";
import useSeo from "@/hooks/useSeo";
import { events } from "@/mocks/events";
import { siteInfo } from "@/mocks/site";
import EventTimeline from "@/pages/events/detail/components/EventTimeline";
import { formatHeat, timelineMeta } from "@/utils/timeline";

const siteUrl = "https://baoliaoqianxian.example.com/";

export default function EventDetail() {
  const { id } = useParams();
  const event = events.find((item) => item.id === id);

  useSeo({
    title: event
      ? `${event.title} - 独家爆料时间线与最新进展`
      : "事件未找到 - 独家爆料",
    description: event
      ? `${event.summary} 本文按时间顺序整理该事件的首发、更新、反转与后续节点，并持续补充最新进展。`
      : "你访问的事件时间线可能已被合并或调整，请返回热点总览查看全部在跟事件。",
    keywords: event
      ? `${event.tags.join(",")},独家爆料,事件时间线,最新进展,反转后续`
      : "独家爆料,热点总览",
    canonicalPath: event ? `/events/${event.id}` : "/events",
    ogType: "article",
    image: event?.cover,
  });

  if (!event) {
    return (
      <section className="max-w-shell mx-auto px-4 md:px-6 py-24 flex flex-col items-center gap-4 text-center">
        <span className="w-14 h-14 rounded-full bg-background-200 text-foreground-500 flex items-center justify-center text-2xl">
          <i className="ri-file-unknow-line" />
        </span>
        <h1 className="font-heading text-2xl font-bold text-foreground-950">未找到这条事件时间线</h1>
        <p className="text-sm text-foreground-600 max-w-md">
          该事件可能已被合并到其他专题，或链接有误。你可以返回热点总览，按分类或关键词重新查找。
        </p>
        <Link
          to="/events"
          className="mt-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          返回热点总览
        </Link>
      </section>
    );
  }

  const related = [
    ...events.filter((item) => item.id !== event.id && item.categorySlug === event.categorySlug),
    ...events.filter((item) => item.id !== event.id && item.categorySlug !== event.categorySlug),
  ].slice(0, 4);
  const ranked = [...events].filter((item) => item.id !== event.id).sort((a, b) => b.heat - a.heat).slice(0, 5);
  const reversalCount = event.timeline.filter((node) => node.type === "reversal").length;

  return (
    <>
      <JsonLd
        id="article"
        data={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: event.title,
          description: event.summary,
          image: [event.cover],
          articleSection: event.category,
          keywords: event.tags.join(","),
          datePublished: event.publishTime.replace(" ", "T"),
          dateModified: event.updateTime.replace(" ", "T"),
          inLanguage: "zh-CN",
          author: { "@type": "Organization", name: siteInfo.name },
          publisher: { "@type": "Organization", name: siteInfo.name },
          mainEntityOfPage: `${siteUrl}events/${event.id}`,
        }}
      />
      <JsonLd
        id="article-breadcrumb"
        data={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: siteUrl },
            { "@type": "ListItem", position: 2, name: "热点总览", item: `${siteUrl}events` },
            { "@type": "ListItem", position: 3, name: event.category, item: `${siteUrl}events?cat=${event.categorySlug}` },
            { "@type": "ListItem", position: 4, name: event.title, item: `${siteUrl}events/${event.id}` },
          ],
        }}
      />

      <section className="border-b border-background-200 paper-grain">
        <div className="max-w-shell mx-auto px-4 md:px-6 py-8 md:py-10">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: "热点总览", to: "/events" },
              { label: event.category, to: `/events?cat=${event.categorySlug}` },
              { label: "事件详情" },
            ]}
          />
          <div className="mt-5 max-w-4xl">
            <Link
              to={`/events?cat=${event.categorySlug}`}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary-50 text-primary-700 border border-primary-100 text-xs hover:bg-primary-100 transition-colors cursor-pointer"
            >
              <i className="ri-price-tag-3-line" />
              {event.category}
            </Link>
            <h1 className="mt-4 font-heading text-2xl md:text-4xl font-black leading-snug tracking-tight text-foreground-950">
              {event.title}
            </h1>
            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs md:text-sm text-foreground-500">
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-calendar-line" />
                首发 {event.publishTime}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-refresh-line" />
                最近更新 {event.updateTime}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-fire-line" />
                热度 {formatHeat(event.heat)}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-chat-3-line" />
                {event.comments} 条讨论
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-user-star-line" />
                {event.source}
              </span>
            </div>
          </div>
        </div>
      </section>

      <section className="py-10 md:py-14">
        <div className="max-w-shell mx-auto px-4 md:px-6 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <article className="lg:col-span-8 flex flex-col gap-10">
            <figure className="rounded-lg overflow-hidden border border-background-200">
              <img
                src={event.cover}
                alt={`${event.title} - ${event.category}事件时间线配图`}
                title={`${event.title} · 独家爆料`}
                className="w-full h-[240px] md:h-[380px] object-cover object-top"
              />
            </figure>

            <div className="rounded-lg bg-background-100 border border-background-200 p-5 md:p-6">
              <span className="inline-flex items-center gap-2 text-xs tracking-widest text-primary-700">
                <i className="ri-file-text-line" />
                事件摘要
              </span>
              <p className="mt-3 text-sm md:text-base leading-relaxed text-foreground-700">{event.summary}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {event.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2.5 py-1 rounded-full bg-background-50 border border-background-300 text-xs text-foreground-600"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>

            <section>
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                  <span className="inline-flex items-center gap-2 text-xs tracking-[0.2em] text-accent-700 uppercase">
                    <span className="w-6 h-px bg-accent-500" />
                    Timeline
                  </span>
                  <h2 className="mt-2 font-heading text-xl md:text-2xl font-bold text-foreground-950">
                    事件时间线
                  </h2>
                </div>
                <div className="flex flex-wrap gap-2 text-[11px]">
                  {(["start", "update", "reversal", "followup"] as const).map((type) => (
                    <span key={type} className={`px-2 py-0.5 rounded-full border ${timelineMeta[type].chip}`}>
                      {timelineMeta[type].label}
                    </span>
                  ))}
                </div>
              </div>
              <div className="mt-6">
                <EventTimeline nodes={event.timeline} />
              </div>
            </section>

            <section>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                这条爆料为什么值得追踪
              </h2>
              <div className="mt-4 flex flex-col gap-4">
                {event.body.map((paragraph, index) => (
                  <p key={index} className="text-sm md:text-base leading-8 text-foreground-700">
                    {paragraph}
                  </p>
                ))}
              </div>
              <div className="mt-6 p-4 rounded-lg bg-secondary-50 border border-secondary-100 flex items-start gap-3">
                <span className="w-8 h-8 rounded-md bg-secondary-100 text-secondary-700 flex items-center justify-center shrink-0">
                  <i className="ri-information-line" />
                </span>
                <p className="text-xs md:text-sm leading-relaxed text-secondary-900">
                  本文共记录 {event.timeline.length} 个时间线节点，其中反转节点 {reversalCount} 个。若你掌握与现有时间线不一致的材料，欢迎在下方联系我们，注明时间与出处，核实后会追加到本条时间线。
                </p>
              </div>
            </section>

            <section>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">相关爆料</h2>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {related.map((item) => (
                  <Link
                    key={item.id}
                    to={`/events/${item.id}`}
                    className="group flex gap-3 p-3 rounded-lg border border-background-200 bg-background-50 hover:border-primary-300 transition-colors cursor-pointer"
                  >
                    <span className="w-20 h-20 rounded-md overflow-hidden shrink-0">
                      <img
                        src={item.cover}
                        alt={`${item.title} - 相关爆料`}
                        title={`${item.title} · 独家爆料`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </span>
                    <span className="flex flex-col gap-1 min-w-0">
                      <span className="text-[11px] text-primary-700">{item.category}</span>
                      <span className="text-sm font-medium leading-snug text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {item.title}
                      </span>
                      <span className="mt-auto text-[11px] text-foreground-400">{item.updateTime}</span>
                    </span>
                  </Link>
                ))}
              </div>
            </section>
          </article>

          <aside className="lg:col-span-4 flex flex-col gap-6">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5 lg:sticky lg:top-24">
              <h2 className="font-heading text-base font-bold text-foreground-950 flex items-center gap-2">
                <i className="ri-fire-line text-primary-500" />
                热度榜
              </h2>
              <div className="mt-4 flex flex-col">
                {ranked.map((item, index) => (
                  <Link
                    key={item.id}
                    to={`/events/${item.id}`}
                    className="group flex items-start gap-3 py-3 border-b border-background-200 last:border-b-0 cursor-pointer"
                  >
                    <span
                      className={`w-6 h-6 rounded flex items-center justify-center text-xs font-bold shrink-0 ${
                        index < 3 ? "bg-accent-500 text-foreground-950" : "bg-background-200 text-foreground-500"
                      }`}
                    >
                      {index + 1}
                    </span>
                    <span className="flex flex-col gap-1 min-w-0">
                      <span className="text-sm leading-snug text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {item.title}
                      </span>
                      <span className="text-[11px] text-foreground-400">
                        {item.category} · 热度 {formatHeat(item.heat)}
                      </span>
                    </span>
                  </Link>
                ))}
              </div>
            </div>

            <div className="rounded-lg bg-foreground-950 p-6 text-background-100">
              <h2 className="font-heading text-base font-bold text-background-50">掌握新进展</h2>
              <p className="mt-2 text-sm leading-relaxed text-background-300/85">
                把新线索或补充材料发给我们，核实后会自动追加到本条时间线，并标注更新来源。
              </p>
              <Link
                to="/about"
                className="mt-4 inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md bg-accent-500 text-foreground-950 text-sm font-medium hover:bg-accent-400 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-send-plane-line" />
                投递补充材料
              </Link>
              <p className="mt-3 text-xs text-background-400">{siteInfo.contactEmail}</p>
            </div>
          </aside>
        </div>
      </section>

      <ScrollTopButton />
    </>
  );
}